import { env } from '@cc-shopping/platform-env';
import type { AnalyticsMarketplacePayload, CtaAnalytics, FinishType } from '../types';
import { SEGMENT_DEPLOYABLE_NAME, SEGMENT_DEPLOYABLE_VERSION } from './constants';

export interface ImpressionOptions {
  navigationId: string;
  parentImpressionId: string;
  impressionPageUrl: string;
  /** AnalyticsPageView page name. IC copies this onto the Shopping Finished event. */
  impressionPageName?: string;
}

export type SearchParamValue =
  | FinishType
  | boolean
  | number
  | AnalyticsMarketplacePayload
  | string
  | undefined;

export enum CTASearchParams {
  TRACKING = 'TRACKING',
  MONETIZING = 'MONETIZING',
  NON_MONETIZING = 'NON_MONETIZING',
  CLICK_HEADER = 'CLICK_HEADER',
  SHOPPING_FINISH = 'SHOPPING_FINISH',
}

type CtaAnalyticsWithoutHref = Omit<CtaAnalytics, 'href'>;

export type SearchParamsGenerator = (
  options: CtaAnalyticsWithoutHref & ImpressionOptions,
) => Record<string, SearchParamValue>;

export const searchParamAggregator = (
  ...builders: SearchParamsGenerator[]
): SearchParamsGenerator => {
  return options => {
    return builders.reduce((paramsAcc, builder) => {
      const newParams = builder(options);
      return {
        ...paramsAcc,
        ...newParams,
      };
    }, {});
  };
};

export type MonetizingParamsAddedByCtaLink = keyof ReturnType<typeof generateMonetizingParams>;

export const generateMonetizingParams = (options: CtaAnalyticsWithoutHref & ImpressionOptions) =>
  ({
    calling_deployable_name: SEGMENT_DEPLOYABLE_NAME,
    calling_deployable_version: env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || SEGMENT_DEPLOYABLE_VERSION,
    cta_type: options.linkType || options.type,
    finish_type: options.finishType,
    ...(options.impressionPageName ? { impression_page_name: options.impressionPageName } : {}),
    impression_page_url: options.impressionPageUrl,
    impression_pageview_id: options.navigationId,
    impression_position: options.productPosition,
    impression_section: options.section?.name,
    monetizable: options.monetizable,
    sku_impression_id: options.parentImpressionId,
  }) as const;

export const generateStandardParams: SearchParamsGenerator = options => ({
  calling_deployable_name: SEGMENT_DEPLOYABLE_NAME,
  calling_deployable_version: env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || SEGMENT_DEPLOYABLE_VERSION,
  cta_type: options.linkType ?? options.type,
  finish_type: options.finishType,
  impression_pageview_id: options.navigationId,
  impression_position: options.productPosition,
  impression_section: options.section?.name,
  monetizable: options.monetizable,
  sku_impression_id: options.parentImpressionId,
});

export const generateNonMonetizingParams: SearchParamsGenerator = options => ({
  navigation_impression_id: options.parentImpressionId,
});

export const PARAM_FUNCS_MAP = {
  [CTASearchParams.MONETIZING]: generateMonetizingParams,
  [CTASearchParams.NON_MONETIZING]: generateNonMonetizingParams,
};

type CtaSearchParamExtra = {
  replacements?: Record<string, SearchParamValue>;
  exclusions?: string[];
};
export type CtaSearchParamAction = CTASearchParams | CtaSearchParamExtra;

export function generateSearchParameters(
  orderedDesiredParams: CtaSearchParamAction[],
): SearchParamsGenerator {
  const builders: SearchParamsGenerator[] = orderedDesiredParams.reduce(
    (acc: SearchParamsGenerator[], targetParam: CtaSearchParamAction) => {
      if (typeof targetParam !== 'object') {
        if (targetParam === CTASearchParams.SHOPPING_FINISH) {
          return acc;
        }
        acc.push(PARAM_FUNCS_MAP[targetParam as keyof typeof PARAM_FUNCS_MAP]);
      }
      return acc;
    },
    [],
  );
  const aggregator = searchParamAggregator(...builders);
  return options => {
    const result: Record<string, SearchParamValue> = aggregator(options);
    return orderedDesiredParams.reduce(
      (
        acc: Record<string, SearchParamValue>,
        extra: CtaSearchParamAction,
      ): Record<string, SearchParamValue> => {
        if (typeof extra !== 'object') {
          return acc;
        }
        if (extra?.exclusions) {
          extra.exclusions.forEach(exclusion => {
            delete acc[exclusion];
          });
        }
        return {
          ...acc,
          ...(extra.replacements ?? {}),
        };
      },
      result,
    );
  };
}
